import { Link, createFileRoute } from '@tanstack/react-router'

import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useDeliveryTrip } from '@/hooks/use-delivery-trips-query'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/delivery-trips/$id',
)({
    component: DeliveryTripDetailPage,
})

function formatDuration(start: string, end?: string | null) {
    const startMs = new Date(start).getTime()
    const endMs = end ? new Date(end).getTime() : Date.now()
    const diff = Math.max(0, endMs - startMs)
    const hours = Math.floor(diff / 3_600_000)
    const minutes = Math.floor((diff % 3_600_000) / 60_000)
    return `${hours}h ${minutes}m`
}

function DeliveryTripDetailPage() {
    const { id } = Route.useParams()
    const { data: trip, isLoading } = useDeliveryTrip(id)

    if (isLoading) {
        return (
            <p className="text-sm text-muted-foreground">
                {m.common_loading()}
            </p>
        )
    }
    if (!trip) {
        return (
            <p className="text-sm text-muted-foreground">
                {m.trip_error_no_active()}
            </p>
        )
    }

    const orders = trip.orders ?? []
    const contractOrders = trip.contract_orders ?? []
    const totalStops = orders.length + contractOrders.length

    return (
        <div className="space-y-4">
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                        <CardTitle className="font-mono">
                            {trip.trip_code}
                        </CardTitle>
                    </div>
                    <Badge
                        variant={
                            trip.status === 'active' ? 'success' : 'secondary'
                        }
                    >
                        {trip.status === 'active'
                            ? m.trip_status_active()
                            : m.trip_status_closed()}
                    </Badge>
                </CardHeader>
                <CardContent>
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div>
                            <dt className="text-xs uppercase text-muted-foreground">
                                {m.trip_driver()}
                            </dt>
                            <dd>{trip.driver_name ?? '—'}</dd>
                        </div>
                        <div>
                            <dt className="text-xs uppercase text-muted-foreground">
                                {m.trip_vehicle()}
                            </dt>
                            <dd>
                                {trip.vehicle
                                    ? `${trip.vehicle.plate_number} — ${trip.vehicle.name}`
                                    : '—'}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-xs uppercase text-muted-foreground">
                                {m.trip_started_at()}
                            </dt>
                            <dd>
                                {new Date(trip.started_at).toLocaleString()}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-xs uppercase text-muted-foreground">
                                {m.trip_ended_at()}
                            </dt>
                            <dd>
                                {trip.ended_at
                                    ? new Date(trip.ended_at).toLocaleString()
                                    : m.trip_running_now()}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-xs uppercase text-muted-foreground">
                                {m.trip_duration()}
                            </dt>
                            <dd>
                                {formatDuration(trip.started_at, trip.ended_at)}
                            </dd>
                        </div>
                        {trip.note && (
                            <div className="sm:col-span-2">
                                <dt className="text-xs uppercase text-muted-foreground">
                                    {m.common_notes()}
                                </dt>
                                <dd>{trip.note}</dd>
                            </div>
                        )}
                    </dl>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>
                        {m.trip_orders_in_trip()} ({totalStops})
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {totalStops === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            {m.trip_no_orders()}
                        </p>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{m.order_code()}</TableHead>
                                    <TableHead>
                                        {m.customer_name_label()}
                                    </TableHead>
                                    <TableHead>
                                        {m.common_status_label()}
                                    </TableHead>
                                    <TableHead className="text-right">
                                        {m.order_total()}
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {orders.map((o) => (
                                    <TableRow key={o.id}>
                                        <TableCell className="font-medium">
                                            <Link
                                                to="/orders/$orderId"
                                                params={{ orderId: o.id }}
                                                className="text-primary hover:underline"
                                            >
                                                {o.order_code}
                                            </Link>
                                        </TableCell>
                                        <TableCell>{o.customer_name}</TableCell>
                                        <TableCell>{o.status}</TableCell>
                                        <TableCell className="text-right">
                                            {o.total_amount.toLocaleString()}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {contractOrders.map((o) => (
                                    <TableRow key={o.id}>
                                        <TableCell className="font-medium">
                                            <Link
                                                to="/contract-deliveries/$id"
                                                params={{ id: o.id }}
                                                className="text-primary hover:underline"
                                            >
                                                {o.order_code}
                                            </Link>
                                        </TableCell>
                                        <TableCell>
                                            {o.recipient_name}
                                            <span className="ml-1 text-xs text-muted-foreground">
                                                {m.cdo_contract_tag()}
                                            </span>
                                        </TableCell>
                                        <TableCell>{o.status}</TableCell>
                                        <TableCell className="text-right">
                                            —
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}
